import { useRef, useCallback, useEffect } from 'react';
import { Howl } from 'howler';

const FADE_DURATION = 2000;
const SHORT_FADE = 800;

interface AmbientEntry {
  howl: Howl;
  file: string;
  volume: number;
}

export interface AudioEngine {
  playMusic: (file: string, volume: number, onEnd?: () => void) => void;
  stopMusic: (fadeMs?: number) => void;
  setMusicVolume: (volume: number) => void;
  playAmbient: (id: string, file: string, volume: number) => void;
  stopAmbient: (id: string, fadeMs?: number) => void;
  setAmbientVolume: (id: string, volume: number) => void;
  stopAll: () => void;
  playPlayerDeath: (onComplete: () => void) => void;
  getMusicVolume: () => number;
}

export function useAudioEngine(): AudioEngine {
  const musicRef = useRef<Howl | null>(null);
  const musicVolumeRef = useRef<number>(0.7);
  const ambientRef = useRef<Map<string, AmbientEntry>>(new Map());
  const deathSoundRef = useRef<Howl | null>(null);

  const stopAll = useCallback(() => {
    if (musicRef.current) {
      const h = musicRef.current;
      h.fade(h.volume() as number, 0, FADE_DURATION);
      setTimeout(() => { try { h.stop(); h.unload(); } catch(e) {} }, FADE_DURATION + 100);
      musicRef.current = null;
    }
    ambientRef.current.forEach((entry) => {
      entry.howl.fade(entry.howl.volume() as number, 0, FADE_DURATION);
      setTimeout(() => { try { entry.howl.stop(); entry.howl.unload(); } catch(e) {} }, FADE_DURATION + 100);
    });
    ambientRef.current.clear();
  }, []);

  const playMusic = useCallback((file: string, volume: number, onEnd?: () => void) => {
    const src = `/audio/music/${file}`;
    musicVolumeRef.current = volume;

    if (musicRef.current) {
      const old = musicRef.current;
      const currentVol = old.volume() as number;
      old.fade(currentVol, 0, FADE_DURATION);
      setTimeout(() => { try { old.stop(); old.unload(); } catch(e) {} }, FADE_DURATION + 100);
    }

    const isVictory = onEnd !== undefined;
    const howl = new Howl({
      src: [src],
      loop: !isVictory,
      volume: 0,
      html5: true,
      onend: onEnd,
      onloaderror: (_id: any, err: any) => {
        console.warn('Could not load music: ' + src, err);
      },
    });

    howl.play();
    howl.fade(0, volume, FADE_DURATION);
    musicRef.current = howl;
  }, []);

  const stopMusic = useCallback((fadeMs = FADE_DURATION) => {
    if (musicRef.current) {
      const old = musicRef.current;
      old.fade(old.volume() as number, 0, fadeMs);
      setTimeout(() => { try { old.stop(); old.unload(); } catch(e) {} }, fadeMs + 100);
      musicRef.current = null;
    }
  }, []);

  const setMusicVolume = useCallback((volume: number) => {
    musicVolumeRef.current = volume;
    if (musicRef.current) {
      musicRef.current.volume(volume);
    }
  }, []);

  const getMusicVolume = useCallback(() => musicVolumeRef.current, []);

  const playAmbient = useCallback((id: string, file: string, volume: number) => {
    const existing = ambientRef.current.get(id);
    if (existing) {
      existing.howl.fade(existing.howl.volume() as number, 0, SHORT_FADE);
      setTimeout(() => { try { existing.howl.stop(); existing.howl.unload(); } catch(e) {} }, SHORT_FADE + 100);
    }

    const src = `/audio/ambient/${file}`;
    const howl = new Howl({
      src: [src],
      loop: true,
      volume: 0,
      html5: true,
      onloaderror: (_id: any, err: any) => {
        console.warn('Could not load ambient: ' + src, err);
      },
    });

    howl.play();
    howl.fade(0, volume, SHORT_FADE);
    ambientRef.current.set(id, { howl, file, volume });
  }, []);

  const stopAmbient = useCallback((id: string, fadeMs = SHORT_FADE) => {
    const existing = ambientRef.current.get(id);
    if (existing) {
      existing.howl.fade(existing.howl.volume() as number, 0, fadeMs);
      setTimeout(() => { try { existing.howl.stop(); existing.howl.unload(); } catch(e) {} }, fadeMs + 100);
      ambientRef.current.delete(id);
    }
  }, []);

  const setAmbientVolume = useCallback((id: string, volume: number) => {
    const existing = ambientRef.current.get(id);
    if (existing) {
      existing.howl.volume(volume);
      existing.volume = volume;
    }
  }, []);

  const playPlayerDeath = useCallback((onComplete: () => void) => {
    const DEATH_FADE = 1500;

    // Fade out all current sounds
    if (musicRef.current) {
      musicRef.current.fade(musicRef.current.volume() as number, 0, DEATH_FADE);
    }
    ambientRef.current.forEach((entry) => {
      entry.howl.fade(entry.howl.volume() as number, 0, DEATH_FADE);
    });

    setTimeout(() => {
      const deathHowl = new Howl({
        src: ['/audio/music/player_death.mp3'],
        loop: false,
        volume: 1.0,
        html5: true,
        onend: () => {
          deathSoundRef.current = null;
          if (musicRef.current) {
            musicRef.current.fade(0, musicVolumeRef.current, FADE_DURATION);
          }
          ambientRef.current.forEach((entry) => {
            entry.howl.fade(0, entry.volume, FADE_DURATION);
          });
          onComplete();
        },
        onloaderror: () => {
          console.warn('Could not load player_death.mp3');
          if (musicRef.current) {
            musicRef.current.fade(0, musicVolumeRef.current, FADE_DURATION);
          }
          ambientRef.current.forEach((entry) => {
            entry.howl.fade(0, entry.volume, FADE_DURATION);
          });
          onComplete();
        },
      });

      deathHowl.play();
      deathSoundRef.current = deathHowl;
    }, DEATH_FADE + 100);
  }, []);

  useEffect(() => {
    return () => {
      try { musicRef.current?.stop(); } catch(e) {}
      ambientRef.current.forEach(e => { try { e.howl.stop(); } catch(e2) {} });
      try { deathSoundRef.current?.stop(); } catch(e) {}
    };
  }, []);

  return {
    playMusic, stopMusic, setMusicVolume, getMusicVolume,
    playAmbient, stopAmbient, setAmbientVolume,
    stopAll, playPlayerDeath,
  };
}
